import {
  ArrowDownIcon,
  Headphones,
  InfoFilledIcon,
  NoHeadsetIcon
} from '@blueprinthq/joy'
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
  VStack
} from '@chakra-ui/react'

import { GridView } from '@components/icons'
import { useAudioCapture, useExperienceManager } from '@hooks'
import { trackEvent } from '@lib/clinician-tracking'
import { Computer } from '@material-ui/icons'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  isChromiumBrowser,
  isWindows
} from '../../../context/sessions-context/utils'
import { TELEHEALTH_TOGGLE_MESSAGES } from '../constants'
import { CustomSwitch } from './shared/custom-switch'
import { TelehealthToggleTooltipContent } from './shared/warning-tooltip'
import { TelehealthSourceInfoModal } from './telehealth-source-info-modal'

export type TelehealthAudioSource = 'browser' | 'desktop'

type WarningConfig = {
  borderColor: string
  backgroundColor: string
  message: string
  showSwitch: boolean
}

interface MenuOption {
  value: string
  label: string
  icon: React.ReactElement
  isDisabled: boolean
  onClick: (value: string) => void
}
interface TelehealthToggleProps {
  isDictation?: boolean
}

export const TelehealthToggle: React.FC<TelehealthToggleProps> = ({
  isDictation = false
}) => {
  const {
    isUsingHeadphones,
    setIsUsingHeadphones,
    isTelehealth,
    setIsTelehealth,
    telehealthAudioSource,
    setTelehealthAudioSource,
    qualityToggle,
    setQualityToggle,
    setActiveWarning,
    telehealthSourceError,
    isTelehealthSourceRequiredEnabled
  } = useAudioCapture()

  const { isAudioShareSourceDropdownEnabled } = useExperienceManager()

  const {
    isOpen: isInfoModalOpen,
    onOpen: onOpenInfoModal,
    onClose: onCloseInfoModal
  } = useDisclosure()

  const handleSettingChange = (newTelehealthValue: boolean) => {
    setActiveWarning(null)
    if (!newTelehealthValue) {
      setIsUsingHeadphones(false)
    }
    setIsTelehealth(newTelehealthValue)
    trackEvent('Confirm Audio Settings Dialog -> Changed Setting Input', {
      newSettingSelection: newTelehealthValue ? 'telehealth' : 'in_person'
    })
  }

  const handleHeadphonesChange = (value: string) => {
    setActiveWarning(null)
    setIsUsingHeadphones(value === 'headphones')
    trackEvent('Confirm Audio Settings Dialog -> Changed Headphones Input', {
      newHeadphonesSelection: value
    })
  }

  const handleSourceChange = (value: string) => {
    setActiveWarning(null)
    setTelehealthAudioSource(value as TelehealthAudioSource)
  }

  const HeadphonesButton = () => (
    <HStack spacing={3}>
      {isUsingHeadphones ? <Headphones /> : <NoHeadsetIcon />}
      <Text color="black">
        {isUsingHeadphones ? 'Using headphones' : 'Not using headphones'}
      </Text>
    </HStack>
  )

  type SourceButtonProps = { hasError?: boolean }

  const SourceButton: React.FC<SourceButtonProps> = ({ hasError = false }) => (
    <HStack spacing={3}>
      {telehealthAudioSource === 'desktop' ? (
        <Icon as={Computer} boxSize={5} color="black" />
      ) : telehealthAudioSource === 'browser' ? (
        <Icon as={GridView} boxSize={5} color="black" />
      ) : (
        <Icon
          as={Computer}
          boxSize={5}
          color={hasError ? '#EB5164' : 'dark_gray'}
        />
      )}
      <Text
        color={
          telehealthAudioSource ? 'black' : hasError ? '#EB5164' : 'dark_gray'
        }
      >
        {telehealthAudioSource === 'desktop'
          ? 'Desktop App (e.g. Zoom, Teams)'
          : telehealthAudioSource === 'browser'
            ? 'Web Browser (e.g. Chrome)'
            : 'What do you use for telehealth?  '}
      </Text>
    </HStack>
  )

  const headphonesOptions: MenuOption[] = [
    {
      value: 'headphones',
      label: 'Using headphones',
      icon: <Headphones />,
      isDisabled: isUsingHeadphones,
      onClick: handleHeadphonesChange
    },
    {
      value: 'no_headphones',
      label: 'Not using headphones',
      icon: <NoHeadsetIcon />,
      isDisabled: !isUsingHeadphones,
      onClick: handleHeadphonesChange
    }
  ]

  const sourceOptions: MenuOption[] = [
    {
      value: 'browser',
      label: 'Web browser (e.g. SimplePractice, Doxy)',
      icon: <Icon as={GridView} boxSize={5} color="black" />,
      isDisabled: telehealthAudioSource === 'browser',
      onClick: handleSourceChange
    },
    {
      value: 'desktop',
      label: 'Desktop app (e.g. Zoom, Teams)',
      icon: <Icon as={Computer} boxSize={5} color="black" />,
      isDisabled: telehealthAudioSource === 'desktop',
      onClick: handleSourceChange
    }
  ]

  const shouldShowMenus = Boolean(isTelehealth) && !isDictation
  const shouldShowSourceMenu =
    Boolean(isTelehealth) &&
    !isDictation &&
    isChromiumBrowser() &&
    isAudioShareSourceDropdownEnabled

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    // Set default values when dropdowns are shown for the first time
    if (shouldShowSourceMenu && !hasInitializedRef.current) {
      if (!telehealthAudioSource && !isTelehealthSourceRequiredEnabled) {
        setTelehealthAudioSource('browser')
      }

      if (qualityToggle === null || qualityToggle === undefined) {
        setQualityToggle(true)
      }

      hasInitializedRef.current = true
    } else if (!shouldShowSourceMenu) {
      // Reset values when dropdowns are hidden
      setTelehealthAudioSource(null)
      setQualityToggle(null)
      hasInitializedRef.current = false
    }
  }, [
    shouldShowSourceMenu,
    setTelehealthAudioSource,
    setQualityToggle,
    telehealthAudioSource,
    qualityToggle,
    isTelehealthSourceRequiredEnabled
  ])

  return (
    <VStack
      spacing={4}
      width="100%"
      mt="0px !important"
      data-testid="telehealth-toggle"
    >
      <ToggleButton
        isTelehealth={Boolean(isTelehealth)}
        onToggle={handleSettingChange}
      />

      <DropdownMenu
        isVisible={shouldShowMenus}
        options={headphonesOptions}
        buttonContent={<HeadphonesButton />}
      />

      <DropdownMenu
        isVisible={shouldShowSourceMenu}
        options={sourceOptions}
        buttonContent={<SourceButton hasError={!!telehealthSourceError} />}
        showInfoIcon={true}
        onInfoClick={onOpenInfoModal}
        errorMessage={telehealthSourceError}
      />

      <SourceWarning
        isTelehealth={Boolean(isTelehealth)}
        isDictation={isDictation}
        isUsingHeadphones={isUsingHeadphones}
        qualityToggle={qualityToggle}
        setQualityToggle={setQualityToggle}
        source={telehealthAudioSource}
      />

      <TelehealthSourceInfoModal
        isOpen={isInfoModalOpen}
        onClose={onCloseInfoModal}
      />
    </VStack>
  )
}

interface ToggleButtonProps {
  isTelehealth: boolean
  onToggle: (value: boolean) => void
}

const ToggleButton: React.FC<ToggleButtonProps> = ({
  isTelehealth,
  onToggle
}) => {
  return (
    <Box w="100%" data-testid="telehealth-toggle-button">
      <Flex
        w="100%"
        h="48px"
        align="center"
        bg="#EAECF4"
        borderRadius="8px"
        _hover={{ cursor: 'pointer' }}
        px={1}
        position="relative"
      >
        <Box
          position="absolute"
          top="5px"
          h="38px"
          w="50%"
          bg="white"
          border="1px solid #D2D4DC"
          borderRadius="4px"
          transform={isTelehealth ? 'translateX(96%)' : 'translateX(0)'}
          transition="transform 0.3s ease-in-out"
        />

        <HStack
          justifyContent="center"
          w="50%"
          h="38px"
          borderRadius="4px"
          onClick={() => onToggle(false)}
          position="relative"
          cursor="pointer"
        >
          <Text
            color={isTelehealth ? '#757575' : 'black'}
            transition="color 0.3s"
          >
            In-Person
          </Text>
        </HStack>
        <HStack
          justifyContent="center"
          spacing={3}
          w="50%"
          h="38px"
          borderRadius="4px"
          onClick={() => onToggle(true)}
          cursor="pointer"
          position="relative"
        >
          <Text
            color={isTelehealth ? 'black' : '#757575'}
            transition="color 0.3s"
          >
            Telehealth
          </Text>
          <Tooltip
            label="Audio is optimized to improve note quality based on your session setting"
            placement="top"
          >
            <Box position="absolute" right="16px">
              <InfoFilledIcon size={'sm'} />
            </Box>
          </Tooltip>
        </HStack>
      </Flex>
    </Box>
  )
}

interface DropdownMenuProps {
  isVisible: boolean
  options: MenuOption[]
  buttonContent: React.ReactNode
  showInfoIcon?: boolean
  onInfoClick?: () => void
  errorMessage?: string | null
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  isVisible,
  options,
  buttonContent,
  showInfoIcon = false,
  onInfoClick,
  errorMessage
}) => {
  const hasError = !!errorMessage

  return (
    <>
      <AnimatedMenu isVisible={isVisible}>
        <Box position="relative" width="100%">
          <Menu placement="bottom-start" matchWidth>
            <MenuButton
              border={`1px solid ${hasError ? '#E53E3E' : '#E4E5E6'}`}
              m="0"
              h="48px"
              w="100%"
              bg="white"
              borderRadius="8px"
              as={Button}
              rightIcon={
                showInfoIcon ? (
                  <HStack spacing={2}>
                    <InfoFilledIcon
                      size="sm"
                      color="black"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        onInfoClick?.()
                      }}
                    />
                    <ArrowDownIcon />
                  </HStack>
                ) : (
                  <ArrowDownIcon />
                )
              }
            >
              {buttonContent}
            </MenuButton>
            <Portal>
              <MenuList w="100%">
                {options.map(option => (
                  <MenuItem
                    key={option.value}
                    isDisabled={option.isDisabled}
                    onClick={() => option.onClick(option.value)}
                    value={option.value}
                    color="black"
                    w="100%"
                    icon={option.icon}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </Box>
      </AnimatedMenu>
      {errorMessage && (
        <Text
          style={{ marginTop: '4px' }}
          w="100%"
          textAlign="left"
          color="#EB5164"
          fontSize="xs"
          fontWeight="medium"
        >
          {errorMessage}
        </Text>
      )}
    </>
  )
}

interface AnimatedMenuProps {
  isVisible: boolean
  children: React.ReactNode
}

const AnimatedMenu: React.FC<AnimatedMenuProps> = ({ isVisible, children }) => {
  return (
    <Box
      w="100%"
      data-testid="animated-menu"
      className="animated-menu"
      sx={{
        height: isVisible ? '48px' : '0px',
        opacity: isVisible ? 1 : 0,
        marginTop: isVisible ? '16px' : '0px !important',
        transition: 'all 0.4s cubic-bezier(0.57, 0.1, 0, 1)',
        overflow: 'hidden'
      }}
    >
      {children}
    </Box>
  )
}

const warningConfigs: Record<string, Record<string, WarningConfig>> = {
  windows: {
    headphonesWebBrowser: {
      borderColor: '#68E19F',
      backgroundColor: 'rgba(104, 225, 159, 0.15)',
      message: TELEHEALTH_TOGGLE_MESSAGES.HEADPHONES_HIGH_QUALITY_APP,
      showSwitch: false
    },
    headphonesDesktopApp: {
      borderColor: '#68E19F',
      backgroundColor: 'rgba(104, 225, 159, 0.15)',
      message: TELEHEALTH_TOGGLE_MESSAGES.SHARE_ENTIRE_SCREEN,
      showSwitch: false
    },
    noHeadphonesWebBrowser: {
      // Unused, included for type compatibility
      borderColor: '',
      backgroundColor: '',
      message: '',
      showSwitch: false
    },
    noHeadphonesLowQualityWebBrowser: {
      borderColor: '#F8D557',
      backgroundColor: '#FEF7DD',
      message: TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_LOW_QUALITY,
      showSwitch: true
    },
    noHeadphonesHighQualityWebBrowser: {
      borderColor: '#68E19F',
      backgroundColor: 'rgba(104, 225, 159, 0.15)',
      message: TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_HIGH_QUALITY_APP,
      showSwitch: true
    },
    noHeadphonesHighQualityDesktop: {
      borderColor: '#68E19F',
      backgroundColor: 'rgba(104, 225, 159, 0.15)',
      message:
        TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_HIGH_QUALITY_DESKTOP_WINDOWS,
      showSwitch: true
    },
    noHeadphonesLowQualityDesktop: {
      borderColor: '#F8D557',
      backgroundColor: '#FEF7DD',
      message: TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_LOW_QUALITY,
      showSwitch: true
    }
  },
  mac: {
    headphonesWebBrowser: {
      borderColor: '#68E19F',
      backgroundColor: 'rgba(104, 225, 159, 0.15)',
      message: TELEHEALTH_TOGGLE_MESSAGES.HEADPHONES_HIGH_QUALITY_APP,
      showSwitch: false
    },
    headphonesDesktopApp: {
      borderColor: '#E53E3E',
      backgroundColor: '#FFF7F6',
      message: TELEHEALTH_TOGGLE_MESSAGES.HEADPHONES_DESKTOP_APP,
      showSwitch: false
    },
    noHeadphonesWebBrowser: {
      // Unused, included for type compatibility
      borderColor: 'null',
      backgroundColor: 'null',
      message: 'null',
      showSwitch: false
    },
    noHeadphonesLowQualityWebBrowser: {
      borderColor: '#F8D557',
      backgroundColor: '#FEF7DD',
      message: TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_LOW_QUALITY,
      showSwitch: true
    },
    noHeadphonesHighQualityWebBrowser: {
      borderColor: '#68E19F',
      backgroundColor: 'rgba(104, 225, 159, 0.15)',
      message: TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_HIGH_QUALITY_APP,
      showSwitch: true
    },
    noHeadphonesHighQualityDesktop: {
      borderColor: '#68E19F',
      backgroundColor: 'rgba(104, 225, 159, 0.15)',
      message:
        TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_HIGH_QUALITY_DESKTOP_WINDOWS,
      showSwitch: true
    },
    noHeadphonesLowQualityDesktop: {
      borderColor: '#F8D557',
      backgroundColor: '#FEF7DD',
      message: TELEHEALTH_TOGGLE_MESSAGES.NO_HEADPHONES_LOW_QUALITY,
      showSwitch: true
    }
  }
}

const SourceWarning = ({
  isTelehealth,
  isDictation,
  isUsingHeadphones,
  qualityToggle,
  setQualityToggle,
  source
}: {
  isTelehealth: boolean
  isDictation: boolean
  isUsingHeadphones: boolean
  qualityToggle: boolean | null
  setQualityToggle: (value: boolean) => void
  source: TelehealthAudioSource | null
}) => {
  const {
    isAudioShareSourceDropdownEnabled,
    isTelehealthSourceRequiredEnabled
  } = useExperienceManager()

  const platform = isWindows() ? 'windows' : 'mac'
  const platformWarningConfigs = warningConfigs[platform]

  // Memoize config to prevent recreating object on every render
  const config = useMemo(() => {
    let configResult: WarningConfig | null = null

    if (!source && isTelehealthSourceRequiredEnabled) {
      if (isUsingHeadphones) {
        configResult = platformWarningConfigs.headphonesWebBrowser
      } else if (qualityToggle) {
        configResult = platformWarningConfigs.noHeadphonesHighQualityWebBrowser
      } else {
        configResult = platformWarningConfigs.noHeadphonesLowQualityWebBrowser
      }
    } else if (isUsingHeadphones) {
      configResult =
        source === 'browser'
          ? platformWarningConfigs.headphonesWebBrowser
          : platformWarningConfigs.headphonesDesktopApp
    } else if (source === 'browser') {
      configResult = qualityToggle
        ? platformWarningConfigs.noHeadphonesHighQualityWebBrowser
        : platformWarningConfigs.noHeadphonesLowQualityWebBrowser
    } else if (isWindows()) {
      // Desktop app with no headphones (Mac) OR headphones on browser (Mac) - don't show any config
      configResult = qualityToggle
        ? platformWarningConfigs.noHeadphonesHighQualityDesktop
        : platformWarningConfigs.noHeadphonesLowQualityDesktop
    }

    return configResult
  }, [
    isUsingHeadphones,
    source,
    qualityToggle,
    platformWarningConfigs,
    isTelehealthSourceRequiredEnabled
  ])

  const isVisible =
    !(!isTelehealth || isDictation || !isAudioShareSourceDropdownEnabled) &&
    isChromiumBrowser() &&
    !!config?.message

  const [displayConfig, setDisplayConfig] = useState<WarningConfig | null>(null)
  const wasVisibleRef = useRef(false)

  useEffect(() => {
    if (isVisible && config) {
      // Update display config immediately when visible
      setDisplayConfig(config)
      wasVisibleRef.current = true
    } else if (!isVisible && wasVisibleRef.current) {
      // When hiding, freeze the current display config for 400ms to allow the transition animation
      const timer = setTimeout(() => {
        setDisplayConfig(null)
        wasVisibleRef.current = false
      }, 400)
      return () => clearTimeout(timer)
    }
  }, [isVisible, config])

  const shouldShow = displayConfig?.message ? isVisible : false

  // Create a unique key for the current configuration
  const configKey = displayConfig
    ? `${displayConfig.message}-${displayConfig.showSwitch}-${qualityToggle}`
    : 'empty'

  return (
    <AnimatedAlert isVisible={shouldShow} contentKey={configKey}>
      {displayConfig ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={2}
          border={`1px solid ${displayConfig.borderColor}`}
          borderRadius="4px"
          p={4}
          w="100%"
          bg={displayConfig.backgroundColor}
          overflow="hidden"
        >
          {displayConfig.showSwitch && (
            <HStack justifyContent="flex-start" w="100%" spacing={3} as="div">
              <CustomSwitch
                isChecked={qualityToggle ?? true}
                onChange={() => setQualityToggle(!(qualityToggle ?? true))}
                onText="ON"
                offText="OFF"
              />
              <Text fontSize="md" fontWeight="medium">
                Highest quality audio
              </Text>
            </HStack>
          )}
          <Box textAlign="left">
            <TelehealthToggleTooltipContent message={displayConfig.message} />
          </Box>
        </Box>
      ) : (
        <div></div>
      )}
    </AnimatedAlert>
  )
}

// Animated wrapper component for smooth animation
const AnimatedAlert: React.FC<{
  isVisible: boolean
  children: React.ReactNode
  contentKey?: string
}> = ({ isVisible, children, contentKey }) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [displayHeight, setDisplayHeight] = useState<number>(0)
  const [shouldRender, setShouldRender] = useState(false)
  const previousKeyRef = useRef<string | undefined>(contentKey)
  const measureTimeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (shouldRender && contentRef.current) {
      const measureAndSetHeight = () => {
        if (contentRef.current) {
          const newHeight = contentRef.current.scrollHeight

          // If content key changed, we need to handle the transition carefully
          if (previousKeyRef.current !== contentKey) {
            // Clear any pending measurement
            if (measureTimeoutRef.current) {
              clearTimeout(measureTimeoutRef.current)
            }

            // Set the new height after a microtask to ensure smooth transition
            measureTimeoutRef.current = setTimeout(() => {
              setDisplayHeight(newHeight)
              previousKeyRef.current = contentKey
            }, 10)
          } else {
            // Initial render or visibility change
            setDisplayHeight(newHeight)
          }
        }
      }

      // Use ResizeObserver for dynamic content changes
      const resizeObserver = new ResizeObserver(measureAndSetHeight)
      resizeObserver.observe(contentRef.current)

      // Initial measurement
      measureAndSetHeight()

      return () => {
        resizeObserver.disconnect()
        if (measureTimeoutRef.current) {
          clearTimeout(measureTimeoutRef.current)
        }
      }
    }
  }, [shouldRender, contentKey])

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true)
    } else {
      // Delay hiding content to allow height animation to complete
      const timer = setTimeout(() => {
        setShouldRender(false)
        setDisplayHeight(0)
      }, 400) // Match the transition duration
      return () => clearTimeout(timer)
    }
  }, [isVisible])

  return (
    <Box
      w="100%"
      sx={{
        height: isVisible ? `${displayHeight}px` : '0px',
        opacity: isVisible ? 1 : 0,
        marginTop: isVisible ? '16px' : '0px !important',
        transition: 'all 0.4s cubic-bezier(0.57, 0.1, 0, 1)',
        overflow: 'hidden'
      }}
    >
      {shouldRender && <div ref={contentRef}>{children}</div>}
    </Box>
  )
}
